import { useMemo } from "react";
import { useAccounts } from "../api/accounts";
import {
  useTransactions,
  useDeleteTransaction,
  useUpdateTransaction,
  useRestoreTransaction,
} from "../api/transactions";
import { useRenameMerchant } from "../api/merchantRules";
import { useCategories } from "../api/categories";
import { AccountCard } from "../components/AccountCard";
import { TransactionRow } from "../components/TransactionRow";
import { BalanceChart } from "../components/BalanceChart";
import { InstallHint } from "../components/InstallHint";
import { VenmoImport } from "../components/VenmoImport";
import { useActiveAlerts } from "../api/alerts";
import { Loading, ErrorState } from "../components/States";
import { useToast } from "../components/Toast";
import { formatCents } from "../lib/format";

export function Dashboard() {
  const accounts = useAccounts();
  const transactions = useTransactions();
  const categories = useCategories();
  const deleteTx = useDeleteTransaction();
  const updateTx = useUpdateTransaction();
  const restoreTx = useRestoreTransaction();
  const renameMerchant = useRenameMerchant();
  const activeAlerts = useActiveAlerts();
  const toast = useToast();
  const categoryNames = categories.data?.categories.map((c) => c.name) ?? [];

  const netWorthCents = useMemo(() => {
    if (!accounts.data) return 0;
    return accounts.data.accounts.reduce((s, a) => s + a.balance_cents, 0);
  }, [accounts.data]);

  const totalStartingCents = useMemo(() => {
    if (!accounts.data) return 0;
    return accounts.data.accounts.reduce((s, a) => s + a.balance_cents, 0);
  }, [accounts.data]);

  const alerts = activeAlerts.data?.active ?? [];

  return (
    <div className="space-y-6">
      <InstallHint />

      {alerts.length > 0 && (
        <section className="space-y-2">
          {alerts.map((a) => (
            <div
              key={a.config_id}
              className="rounded-lg border border-amber-700/60 bg-amber-950/30 p-3 text-sm"
            >
              <div className="font-medium text-amber-200">{a.title}</div>
              <div className="text-amber-100/80">{a.detail}</div>
            </div>
          ))}
        </section>
      )}

      <VenmoImport />

      <section>
        <div className="text-xs uppercase tracking-wide text-slate-500">Net worth</div>
        <div className="text-3xl font-semibold tabular-nums mt-1">
          {accounts.isPending ? "—" : formatCents(netWorthCents)}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-medium text-slate-300 mb-2">Balance over time</h2>
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
          <BalanceChart
            transactions={transactions.data?.transactions ?? []}
            startingBalanceCents={totalStartingCents}
          />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-medium text-slate-300 mb-2">Accounts</h2>
        {accounts.isPending && <Loading />}
        {accounts.isError && <ErrorState onRetry={() => accounts.refetch()} />}
        {accounts.data?.accounts.length === 0 && (
          <div className="text-slate-500 text-sm">
            No accounts yet — add one in Settings.
          </div>
        )}
        <div className="space-y-2">
          {accounts.data?.accounts.map((a) => (
            <AccountCard key={a.id} account={a} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-medium text-slate-300 mb-2">Recent transactions</h2>
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4">
          {transactions.isPending && <Loading className="py-3" />}
          {transactions.isError && (
            <div className="py-3">
              <ErrorState onRetry={() => transactions.refetch()} />
            </div>
          )}
          {transactions.data?.transactions.length === 0 && (
            <div className="text-slate-500 text-sm py-3">
              No transactions yet.
            </div>
          )}
          {transactions.data?.transactions.slice(0, 20).map((tx) => (
            <TransactionRow
              key={tx.id}
              tx={tx}
              categories={categoryNames}
              onSave={async (patch) => {
                await updateTx.mutateAsync({ id: tx.id, patch });
                toast("Transaction updated");
              }}
              onRename={async (input) => {
                await renameMerchant.mutateAsync(input);
                toast("Merchant renamed");
              }}
              onDelete={(id) =>
                deleteTx.mutate(id, {
                  onSuccess: () =>
                    toast("Transaction deleted", {
                      actionLabel: "Undo",
                      onAction: () => restoreTx.mutate(id),
                      durationMs: 6000,
                    }),
                })
              }
            />
          ))}
        </div>
      </section>
    </div>
  );
}
